// src/pages/RoomsPage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Alert,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { api } from '../services/api';
import { useTranslation } from 'react-i18next';

interface Guest {
  id: string;
  firstName: string;
  lastName: string;
  role: string;
}

interface Room {
  id: string;
  roomNumber: string;
  roomType?: string;
  floor?: string;
  status: string;
  localityId?: string;
  capacity: number;
  guests?: Guest[];
}

const RoomsPage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [rooms, setRooms] = useState<Room[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingRoom, setEditingRoom] = useState<Room | null>(null);

  // Form state
  const [roomNumber, setRoomNumber] = useState('');
  const [roomType, setRoomType] = useState('');
  const [floor, setFloor] = useState('');
  const [status, setStatus] = useState('available');
  const [localityId, setLocalityId] = useState('');
  const [capacity, setCapacity] = useState(2);

  // Common TextField styling
  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      backgroundColor: isDark ? theme.palette.background.paper : '#ffffff',
      '&:hover': {
        backgroundColor: isDark ? theme.palette.background.paper : '#ffffff',
        '& fieldset': {
          borderColor: isDark ? 'rgba(255, 255, 255, 0.3)' : '#cbd5e1',
        },
      },
      '&.Mui-focused': {
        backgroundColor: isDark ? theme.palette.background.paper : '#ffffff',
        '& fieldset': {
          borderColor: isDark ? '#3b82f6' : '#667eea',
          borderWidth: 2,
        },
      },
      '& fieldset': {
        borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0',
        borderWidth: 1,
      },
    },
    '& .MuiInputLabel-root': {
      fontWeight: 500,
      color: isDark ? theme.palette.text.secondary : '#64748b',
      zIndex: 1,
      '&.Mui-focused': {
        color: isDark ? '#3b82f6' : '#667eea',
        fontWeight: 500,
      },
      '&.MuiInputLabel-shrink': {
        backgroundColor: isDark ? theme.palette.background.paper : '#ffffff',
        paddingLeft: '4px',
        paddingRight: '4px',
      },
    },
    '& .MuiInputBase-input': {
      fontWeight: 400,
      color: theme.palette.text.primary,
    },
  };

  const fetchRooms = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/rooms');
      setRooms(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || t('rooms.failedToFetch'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchRooms();
  }, [fetchRooms]);

  const handleOpenDialog = (room?: Room) => {
    if (room) {
      setEditingRoom(room);
      setRoomNumber(room.roomNumber);
      setRoomType(room.roomType || '');
      setFloor(room.floor || '');
      setStatus(room.status);
      setLocalityId(room.localityId || '');
      setCapacity(room.capacity || 2);
    } else {
      setEditingRoom(null);
      setRoomNumber('');
      setRoomType('');
      setFloor('');
      setStatus('available');
      setLocalityId('');
      setCapacity(2);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingRoom(null);
    setError('');
    setSuccess('');
  };

  const handleSubmit = async () => {
    try {
      setError('');
      setSuccess('');

      if (!roomNumber.trim()) {
        setError(t('rooms.roomNumberRequired'));
        return;
      }

      const roomData = {
        roomNumber: roomNumber.trim(),
        roomType: roomType.trim() || undefined,
        floor: floor.trim() || undefined,
        status,
        localityId: localityId.trim() || undefined,
        capacity: Number(capacity) || 2,
      };

      if (editingRoom) {
        await api.put(`/rooms/${editingRoom.id}`, roomData);
        setSuccess(t('rooms.roomUpdated'));
      } else {
        await api.post('/rooms', roomData);
        setSuccess(t('rooms.roomCreated'));
      }

      await fetchRooms();
      handleCloseDialog();
    } catch (err: any) {
      setError(err.response?.data?.message || t('rooms.failedToSave'));
    }
  };

  const handleDelete = async (roomId: string) => {
    if (!window.confirm(t('rooms.deleteRoom'))) {
      return;
    }

    try {
      setError('');
      await api.delete(`/rooms/${roomId}`);
      setSuccess(t('rooms.roomDeleted'));
      await fetchRooms();
    } catch (err: any) {
      setError(err.response?.data?.message || t('rooms.failedToSave'));
    }
  };

  const getRoomOccupancy = (room: Room) => {
    // Count only actual guests, exclude operators and admins
    const guestCount = room.guests?.filter(user => user.role === 'guest').length || 0;
    const capacity = room.capacity || 2;
    return { guestCount, capacity };
  };

  const getDynamicStatus = (room: Room) => {
    const { guestCount, capacity } = getRoomOccupancy(room);
    
    // If room is in maintenance, keep that status
    if (room.status === 'maintenance') {
      return 'maintenance';
    }
    
    // Dynamic status based on occupancy
    if (guestCount === 0) {
      return 'available';
    } else if (guestCount >= capacity) {
      return 'full';
    } else {
      return 'occupied';
    }
  };

  const getStatusColor = (status: string) => {
    if (isDark) {
      switch (status) {
        case 'available':
          return { bg: '#14532d', color: '#4ade80', border: '#16a34a' };
        case 'occupied':
          return { bg: '#78350f', color: '#fbbf24', border: '#f59e0b' };
        case 'full':
          return { bg: '#7f1d1d', color: '#f87171', border: '#ef4444' };
        case 'maintenance':
          return { bg: '#1e3a8a', color: '#60a5fa', border: '#3b82f6' };
        default:
          return { bg: '#1f2937', color: '#9ca3af', border: '#374151' };
      }
    } else {
      switch (status) {
        case 'available':
          return { bg: '#d1fae5', color: '#065f46', border: '#a7f3d0' };
        case 'occupied':
          return { bg: '#fef3c7', color: '#92400e', border: '#fde68a' };
        case 'full':
          return { bg: '#fee2e2', color: '#991b1b', border: '#fecaca' };
        case 'maintenance':
          return { bg: '#dbeafe', color: '#1e40af', border: '#bfdbfe' };
        default:
          return { bg: '#f1f5f9', color: '#475569', border: '#e2e8f0' };
      }
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'available':
        return t('rooms.available');
      case 'occupied':
        return t('rooms.occupied');
      case 'full':
        return t('rooms.full');
      case 'maintenance':
        return t('rooms.maintenance');
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4, bgcolor: theme.palette.background.default }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 600,
            color: theme.palette.text.primary,
          }}
        >
          {t('rooms.title')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            borderRadius: 2,
            bgcolor: isDark ? '#1e3b8a' : '#3b82f6',
            boxShadow: 'none',
            '&:hover': {
              bgcolor: isDark ? '#1e40af' : '#2563eb',
              boxShadow: 'none',
            },
          }}
        >
          {t('rooms.addRoom')}
        </Button>
      </Box>

      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 2, borderRadius: 2 }} 
          onClose={() => setError('')}
        >
          {error}
        </Alert>
      )}
      {success && (
        <Alert 
          severity="success" 
          sx={{ mb: 2, borderRadius: 2 }} 
          onClose={() => setSuccess('')}
        >
          {success}
        </Alert>
      )}

      {rooms.length === 0 ? (
        <Paper 
          sx={{ 
            p: 4, 
            textAlign: 'center',
            borderRadius: 2,
            bgcolor: theme.palette.background.paper,
            boxShadow: isDark ? '0 1px 3px rgba(0, 0, 0, 0.3)' : '0 1px 3px rgba(0, 0, 0, 0.1)',
            border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #e2e8f0',
          }}
        >
          <Typography 
            variant="h6" 
            sx={{ 
              color: theme.palette.text.secondary, 
              fontWeight: 600,
              mb: 1,
            }}
          >
            {t('rooms.noRoomsYet')}
          </Typography>
          <Typography 
            sx={{ 
              color: theme.palette.text.secondary, 
              mb: 2,
              fontWeight: 400,
            }}
          >
            {t('rooms.createFirstRoom')}
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />} 
            onClick={() => handleOpenDialog()}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              borderRadius: 2,
              bgcolor: isDark ? '#1e3b8a' : '#3b82f6',
              boxShadow: 'none',
              '&:hover': {
                bgcolor: isDark ? '#1e40af' : '#2563eb',
                boxShadow: 'none',
              },
            }}
          >
            {t('rooms.createFirstRoomButton')}
          </Button>
        </Paper>
      ) : (
        <TableContainer 
          component={Paper}
          sx={{
            borderRadius: 2,
            bgcolor: theme.palette.background.paper,
            boxShadow: isDark ? '0 1px 3px rgba(0, 0, 0, 0.3)' : '0 1px 3px rgba(0, 0, 0, 0.1)',
            border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #e2e8f0',
          }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: isDark ? 'rgba(255, 255, 255, 0.03)' : '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 600, color: theme.palette.text.primary }}>{t('rooms.roomNumber')}</TableCell>
                <TableCell sx={{ fontWeight: 600, color: theme.palette.text.primary }}>{t('rooms.type')}</TableCell>
                <TableCell sx={{ fontWeight: 600, color: theme.palette.text.primary }}>{t('rooms.floor')}</TableCell>
                <TableCell sx={{ fontWeight: 600, color: theme.palette.text.primary }}>{t('rooms.capacity')}</TableCell>
                <TableCell sx={{ fontWeight: 600, color: theme.palette.text.primary }}>{t('rooms.occupancy')}</TableCell>
                <TableCell sx={{ fontWeight: 600, color: theme.palette.text.primary }}>{t('rooms.status')}</TableCell>
                <TableCell sx={{ fontWeight: 600, color: theme.palette.text.primary }}>{t('rooms.localityIdPms')}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>{t('rooms.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rooms.map((room) => {
                const { guestCount, capacity } = getRoomOccupancy(room);
                const dynamicStatus = getDynamicStatus(room);
                const statusColors = getStatusColor(dynamicStatus);
                
                return (
                  <TableRow 
                    key={room.id}
                    sx={{
                      '&:hover': {
                        bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc',
                      },
                    }}
                  >
                    <TableCell>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                        {room.roomNumber}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ color: theme.palette.text.secondary }}>{room.roomType || '—'}</TableCell>
                    <TableCell sx={{ color: theme.palette.text.secondary }}>{room.floor || '—'}</TableCell>
                    <TableCell>
                      <Chip
                        label={capacity}
                        size="small"
                        sx={{
                          bgcolor: isDark ? 'rgba(255, 255, 255, 0.1)' : '#f1f5f9',
                          color: isDark ? theme.palette.text.secondary : '#475569',
                          fontWeight: 600,
                          border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #e2e8f0',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={`${guestCount}/${capacity}`}
                          size="small"
                          sx={{
                            bgcolor: guestCount === 0 
                              ? (isDark ? 'rgba(255, 255, 255, 0.1)' : '#f1f5f9')
                              : guestCount >= capacity 
                                ? (isDark ? '#7f1d1d' : '#fee2e2')
                                : (isDark ? '#78350f' : '#fef3c7'),
                            color: guestCount === 0 
                              ? (isDark ? theme.palette.text.secondary : '#475569')
                              : guestCount >= capacity 
                                ? (isDark ? '#f87171' : '#991b1b')
                                : (isDark ? '#fbbf24' : '#92400e'),
                            fontWeight: 600,
                            border: guestCount === 0 
                              ? (isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #e2e8f0')
                              : guestCount >= capacity 
                                ? (isDark ? '1px solid #ef4444' : '1px solid #fecaca')
                                : (isDark ? '1px solid #f59e0b' : '1px solid #fde68a'),
                          }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(dynamicStatus)}
                        size="small"
                        sx={{
                          bgcolor: statusColors.bg,
                          color: statusColors.color,
                          fontWeight: 600,
                          border: `1px solid ${statusColors.border}`,
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: theme.palette.text.secondary }}>{room.localityId || '—'}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(room)}
                        sx={{
                          color: isDark ? '#60a5fa' : '#3b82f6',
                          '&:hover': {
                            bgcolor: isDark ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.1)',
                          },
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(room.id)}
                        sx={{
                          color: isDark ? '#f87171' : '#ef4444',
                          '&:hover': {
                            bgcolor: isDark ? 'rgba(248, 113, 113, 0.2)' : '#fef2f2',
                          },
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create/Edit Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            bgcolor: theme.palette.background.paper,
            boxShadow: isDark ? '0 4px 6px rgba(0, 0, 0, 0.5)' : '0 4px 6px rgba(0, 0, 0, 0.1)',
          },
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: 600,
          color: theme.palette.text.primary,
          fontSize: '1.25rem',
          borderBottom: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #e2e8f0',
          pb: 2,
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
            {editingRoom ? t('rooms.editRoom') : t('rooms.createNewRoom')}
            <IconButton 
              onClick={handleCloseDialog} 
              size="small"
              sx={{
                color: theme.palette.text.secondary,
                '&:hover': {
                  bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f1f5f9',
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField
              label={t('rooms.roomNumber')}
              value={roomNumber}
              onChange={(e) => setRoomNumber(e.target.value)}
              required
              fullWidth
              helperText={t('rooms.roomNumberHelper')}
              sx={textFieldSx}
            />

            <TextField
              label={t('rooms.type')}
              value={roomType}
              onChange={(e) => setRoomType(e.target.value)}
              fullWidth
              helperText={t('rooms.roomTypeHelper')}
              sx={textFieldSx}
            />

            <TextField
              label={t('rooms.floor')}
              value={floor}
              onChange={(e) => setFloor(e.target.value)}
              fullWidth
              helperText={t('rooms.floorHelper')}
              sx={textFieldSx}
            />

            <TextField
              label={t('rooms.capacity')}
              type="number"
              value={capacity}
              onChange={(e) => setCapacity(parseInt(e.target.value) || 1)}
              fullWidth
              inputProps={{ min: 1, max: 20 }}
              helperText={t('rooms.capacityHelper')}
              sx={textFieldSx}
            />

            <FormControl fullWidth sx={textFieldSx}>
              <InputLabel>{t('rooms.status')}</InputLabel>
              <Select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                label={t('rooms.status')}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      borderRadius: 2,
                      mt: 1,
                      bgcolor: theme.palette.background.paper,
                      boxShadow: isDark ? '0 4px 6px rgba(0, 0, 0, 0.5)' : '0 4px 6px rgba(0, 0, 0, 0.1)',
                    },
                  },
                }}
              >
                <MenuItem value="available">{t('rooms.available')}</MenuItem>
                <MenuItem value="occupied">{t('rooms.occupied')}</MenuItem>
                <MenuItem value="maintenance">{t('rooms.maintenance')}</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label={t('rooms.localityIdPms')}
              value={localityId}
              onChange={(e) => setLocalityId(e.target.value)}
              fullWidth
              helperText={t('rooms.localityIdHelper')}
              sx={textFieldSx}
            />

            {error && (
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                {error}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1, borderTop: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #e2e8f0' }}>
          <Button 
            onClick={handleCloseDialog}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              borderRadius: 2,
              px: 2.5,
              color: theme.palette.text.secondary,
              '&:hover': {
                bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f1f5f9',
              },
            }}
          >
            {t('login.cancel')}
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              borderRadius: 2,
              px: 2.5,
              bgcolor: isDark ? '#1e3b8a' : '#3b82f6',
              boxShadow: 'none',
              '&:hover': {
                bgcolor: isDark ? '#1e40af' : '#2563eb',
                boxShadow: 'none',
              },
            }}
          >
            {editingRoom ? t('rooms.updateRoom') : t('rooms.createRoom')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default RoomsPage;

